import { isValidElement, useContext, useMemo } from 'react'
import type { ReactNode } from 'react'
import clsx from 'clsx'
import { convertJsxToString } from '../../../../shared/component-helper'
import { Lead } from '../../../../elements'
import type { FlexContainerAllProps as FlexContainerProps } from '../../../../components/flex/Container'
import IterateItemContext from '../IterateItemContext'
import type { ArrayItemAreaProps } from '../Array/ArrayItemArea'
import ArrayItemArea from '../Array/ArrayItemArea'
import Toolbar from '../Toolbar'
import { useSwitchContainerMode } from '../hooks'
import DoneButton from './DoneButton'
import CancelButton, { useWasNew } from './CancelButton'
import { replaceItemNo } from '../ItemNo'
import withComponentMarkers from '../../../../shared/helpers/withComponentMarkers'

export type IterateEditContainerProps = {
  /**
   * The title of the EditContainer.
   */
  title?: ReactNode

  /**
   * The title for a new item show within the EditContainer.
   */
  titleWhenNew?: ReactNode

  /**
   * If the EditContainer is open or not.
   */
  open?: boolean

  /**
   * An alternative toolbar to be shown in the EditContainer.
   */
  toolbar?: ReactNode

  /**
   * The variant of the toolbar.
   */
  toolbarVariant?: ArrayItemAreaProps['toolbarVariant']
}

export type IterateEditContainerAllProps = IterateEditContainerProps &
  Omit<FlexContainerProps, 'onAnimationEnd'> &
  ArrayItemAreaProps

export default function EditContainer(
  props: IterateEditContainerAllProps
) {
  const { toolbar, toolbarVariant, children, ...rest } = props
  const { arrayValue } = useContext(IterateItemContext)

  let toolbarElement = toolbar
  if (toolbarVariant === 'minimumOneItem' && arrayValue.length <= 1) {
    toolbarElement = <></>
  }

  const hasToolbar =
    !toolbarElement &&
    (Array.isArray(children) ? children : [children]).some(
      (child) => isValidElement(child) && child.type === Toolbar
    )

  return (
    <EditContainerWithoutToolbar
      toolbar={
        hasToolbar
          ? null
          : (toolbarElement ??
            (toolbarVariant !== 'custom' && (
              <Toolbar>
                <DoneButton />
                <CancelButton />
              </Toolbar>
            )))
      }
      toolbarVariant={toolbarVariant}
      {...rest}
    >
      {children}
    </EditContainerWithoutToolbar>
  )
}

export function EditContainerWithoutToolbar(
  props: IterateEditContainerProps &
    Omit<FlexContainerProps, 'onAnimationEnd'> & {
      toolbar?: ReactNode
    }
) {
  const { containerMode, isNew, index, path } =
    useContext(IterateItemContext)

  const {
    children,
    className,
    title,
    titleWhenNew,
    toolbar,
    toolbarVariant,
    ...restProps
  } = props || {}

  const wasNew = useWasNew({ isNew, containerMode })
  const itemTitle = useMemo(() => {
    return replaceItemNo(
      wasNew && titleWhenNew ? titleWhenNew : title,
      index
    )
  }, [index, title, titleWhenNew, wasNew])

  useSwitchContainerMode(path)

  return (
    <ArrayItemArea
      mode="edit"
      className={clsx('dnb-forms-section-edit-block', className)}
      ariaLabel={convertJsxToString(itemTitle)}
      toolbarVariant={toolbarVariant}
      {...restProps}
    >
      {itemTitle && <Lead size="basis">{itemTitle}</Lead>}
      {children}
      {toolbar}
    </ArrayItemArea>
  )
}

EditContainer.DoneButton = DoneButton
EditContainer.CancelButton = CancelButton

withComponentMarkers(EditContainer, { _supportsSpacingProps: true })
withComponentMarkers(EditContainerWithoutToolbar, {
  _supportsSpacingProps: true,
})
